// Modal dedicado pra atribuir membros a um grupo (separado da gestão de
// bancos e do form completo de edição). Mesmo padrão de GroupBanksSection:
// quick-edit com check-toggle e auto-save a cada clique, sem precisar
// confirmar com "Salvar" no final.
//
// Por que isso existe: o admin reportou que matricular vários alunos via
// "Editar grupo" era cansativo (toggle + Salvar a cada vez). Aqui ele
// abre a lista, marca/desmarca e a UI commit imediatamente.

import { useMemo, useState } from "preact/hooks";
import { Check, Loader2, Search, Users, X } from "lucide-react";
import { api, type GroupDetail, type UserRow } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

export function GroupMembersModal({
  group,
  allUsers,
  onClose
}: {
  group: GroupDetail;
  allUsers: UserRow[];
  onClose: () => void;
}) {
  const [memberIds, setMemberIds] = useState<string[]>(
    group.members.map((m) => m.id)
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");

  const userById = useMemo(() => {
    const m = new Map<string, UserRow>();
    for (const u of allUsers) m.set(u.id, u);
    return m;
  }, [allUsers]);

  const selectedSet = new Set(memberIds);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const candidates = allUsers;
    if (!term) {
      // Ordena: selecionados no topo
      return [...candidates].sort((a, b) => {
        const aSel = selectedSet.has(a.id) ? 0 : 1;
        const bSel = selectedSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return a.name.localeCompare(b.name);
      });
    }
    return candidates
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(term)
      )
      .slice(0, 100);
  }, [allUsers, q, memberIds]);

  async function toggle(userId: string) {
    const isMember = memberIds.includes(userId);
    setBusy(userId);
    try {
      if (isMember) {
        await api.patchGroup(group.id, { removeMembers: [userId] });
        setMemberIds(memberIds.filter((x) => x !== userId));
      } else {
        await api.patchGroup(group.id, { addMembers: [userId] });
        setMemberIds([...memberIds, userId]);
      }
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(null);
    }
  }

  async function sync() {
    setSyncing(true);
    setInfo(null);
    try {
      const r = await api.syncGroupGrants(group.id);
      if (r.message) {
        setInfo(`✓ ${r.message}`);
      } else {
        setInfo(
          `✓ ${r.granted} grant${r.granted !== 1 ? "s" : ""} novo${r.granted !== 1 ? "s" : ""} criado${r.granted !== 1 ? "s" : ""} (${r.skipped} já existiam) — ${r.members} membro${r.members !== 1 ? "s" : ""} × ${r.banks} banco${r.banks !== 1 ? "s" : ""}.`
        );
      }
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSyncing(false);
    }
  }

  const selectedUsers = memberIds
    .map((id) => userById.get(id))
    .filter((u): u is UserRow => !!u);

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Users width="1.2em" height="1.2em" className="text-primary" />
            Membros do grupo: {group.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          <div class="rounded-md border border-border bg-card/40 p-3">
            <div class="mb-2 flex items-center justify-between">
              <h3 class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {selectedUsers.length} membro{selectedUsers.length !== 1 ? "s" : ""}
              </h3>
              <p class="text-[10px] text-muted-foreground">
                Cada clique salva imediatamente.
              </p>
            </div>
            <div class="relative">
              <Search
                width="1em"
                height="1em"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                value={q}
                onInput={(e) => setQ((e.target as HTMLInputElement).value)}
                placeholder="Buscar por email, nome…"
                aria-label="Buscar usuário"
                class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </div>
            <div class="mt-2 max-h-[400px] overflow-y-auto rounded-md border border-border bg-background">
              {filtered.length === 0 ? (
                <p class="p-3 text-center text-xs italic text-muted-foreground">
                  Nenhum usuário encontrado.
                </p>
              ) : (
                <ul class="divide-y divide-border">
                  {filtered.map((u) => {
                    const checked = selectedSet.has(u.id);
                    const isBusy = busy === u.id;
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => toggle(u.id)}
                          disabled={isBusy}
                          class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                            checked
                              ? "bg-primary/10 hover:bg-primary/15"
                              : "hover:bg-accent/30"
                          }`}
                        >
                          <span
                            class={`inline-flex size-4 shrink-0 items-center justify-center rounded border ${
                              checked
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-background"
                            }`}
                            aria-hidden="true"
                          >
                            {checked && <Check width="0.75em" height="0.75em" />}
                          </span>
                          <span class="min-w-0 flex-1">
                            <span class="block truncate font-semibold">
                              {u.name}
                            </span>
                            <span class="block truncate font-mono text-[10px] text-muted-foreground">
                              {u.email}
                            </span>
                          </span>
                          {isBusy && (
                            <Loader2
                              width="0.9em"
                              height="0.9em"
                              className="animate-spin text-muted-foreground"
                            />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <section class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
            <div class="flex items-start gap-3">
              <Users
                width="1.2em"
                height="1.2em"
                className="mt-0.5 shrink-0 text-violet-600 dark:text-violet-300"
              />
              <div class="min-w-0 flex-1">
                <div class="text-xs font-bold text-violet-700 dark:text-violet-300">
                  Sincronizar acesso aos bancos do grupo
                </div>
                <p class="mt-0.5 text-[11px] text-muted-foreground">
                  Materializa grants pra cada par (membro × banco) do grupo.
                  Idempotente — não toca grants existentes.
                </p>
              </div>
              <button
                type="button"
                onClick={sync}
                disabled={syncing || memberIds.length === 0}
                class="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/25 disabled:opacity-50 dark:text-violet-300"
              >
                {syncing ? (
                  <Loader2 width="1em" height="1em" className="animate-spin" />
                ) : (
                  <Users width="1em" height="1em" />
                )}
                Sincronizar
              </button>
            </div>
          </section>

          {info && (
            <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              {info}
            </div>
          )}
        </div>

        <div class="border-t border-border bg-card px-4 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
